import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Breadcrumb from '../../ui/Breadcrumb'
import { getFullAttachmentUrl } from '@services/api/index.js'

const PartnerDetail = ({ partner }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [imageLoaded, setImageLoaded] = useState(false)
  const [imageError, setImageError] = useState(false)

  if (!partner) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('partners.states.loading')}</p>
        </div>
      </div>
    )
  }

  const handleImageLoad = () => {
    setImageLoaded(true)
  }

  const handleImageError = () => {
    setImageError(true)
  }

  const getRegionTagStyle = (inlandFlag) => {
    return inlandFlag === '1' 
      ? 'bg-green-100 text-green-800' 
      : 'bg-purple-100 text-purple-800'
  }

  const getRegionText = (inlandFlag) => {
    return inlandFlag === '1' 
      ? t('partners.regions.domestic') 
      : t('partners.regions.international')
  }

  // 面包屑导航数据
  const breadcrumbItems = [
    { label: t('partners.detail.breadcrumb.home'), path: '/' },
    { label: t('partners.detail.breadcrumb.partners'), path: '/partners' },
    { label: partner.orgName }
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* 返回按钮 */}
        <button
          onClick={() => navigate('/partners')}
          className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 
                     transition-colors mb-6 group"
        >
          <svg className="w-5 h-5 group-hover:-translate-x-1 transition-transform" 
               fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          <span>{t('partners.detail.backToList')}</span>
        </button>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {/* 头部概览 */}
          <div className="p-6 lg:p-8 border-b border-gray-200">
            <div className="flex flex-col lg:flex-row lg:items-start gap-6">
              {/* Logo */}
              <div className="flex-shrink-0">
                <div className="w-24 h-24 bg-gray-50 rounded-lg p-4 flex items-center justify-center">
                  {partner.logoId && !imageError ? (
                    <img
                      src={getFullAttachmentUrl(partner.logoId)}
                      alt={partner.orgName}
                      className={`w-full h-full object-contain transition-opacity duration-300 ${
                        imageLoaded ? 'opacity-100' : 'opacity-0'
                      }`}
                      onLoad={handleImageLoad}
                      onError={handleImageError}
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-200 rounded flex items-center justify-center">
                      <span className="text-gray-400 text-sm">Logo</span>
                    </div>
                  )}
                </div>
              </div>

              {/* 基本信息 */}
              <div className="flex-1">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
                  <div>
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">{partner.orgName}</h1>
                  </div>
                  
                  <div className="flex gap-2">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getRegionTagStyle(partner.inlandFlag)}`}>
                      {getRegionText(partner.inlandFlag)}
                    </span>
                  </div>
                </div>

                {/* 基础信息网格 */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-sm text-gray-600 mb-1">{t('partners.detail.info.location')}</div>
                    <div className="font-medium text-gray-900">{partner.address}</div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-sm text-gray-600 mb-1">{t('partners.detail.info.contactPerson')}</div>
                    <div className="font-medium text-gray-900">{partner.linkman}</div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-sm text-gray-600 mb-1">{t('partners.detail.info.email')}</div>
                    <div className="font-medium text-gray-900">{partner.email}</div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-sm text-gray-600 mb-1">{t('partners.detail.info.phone')}</div>
                    <div className="font-medium text-gray-900">{partner.phone}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 详细信息区域 */}
          <div className="p-6 lg:p-8">
            {/* 主要服务 */}
            {partner.service && (
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{t('partners.detail.info.mainServices')}</h3>
                <div className="flex flex-wrap gap-2">
                  <span 
                    className="bg-blue-50 text-blue-700 px-3 py-2 rounded-lg text-sm font-medium"
                  >
                    {partner.service}
                  </span>
                </div>
              </div>
            )}

            {/* 机构描述 */}
            {partner.orgDesc && (
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{t('partners.detail.info.orgIntro')}</h3>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <div className="text-gray-700 leading-relaxed">{partner.orgDesc}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default PartnerDetail 